'use client'

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { User } from '@/lib/types'
import { MessageCircleIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { ChatKitPageWrapper } from '../_components/chatkit-page-wrapper'
import { ChatKitLoginGate } from '../_components/chatkit-login-gate'
import { getChatKitConstants } from './_const'

interface ISocialMediaGeneratorClientProps {
  user: User | null
  userApiKey: string
  hasApiKey: boolean
}

/**
 * Client component for Social Media Generator
 * Handles ChatKit integration and user interactions
 */
export default function SocialMediaGeneratorClient({
  user,
  userApiKey,
  hasApiKey,
}: ISocialMediaGeneratorClientProps) {
  const t = useTranslations('social-media')
  const constants = getChatKitConstants(t)

  if (!user) {
    return (
      <ChatKitLoginGate
        icon={MessageCircleIcon}
        title="Log in to plan social posts with AI"
        description="Preview the Social Media Agent’s hooks and calendars. Sign in to schedule, save templates, and collaborate with your team."
        redirectUrl="/chatkit/social-media"
      />
    )
  }

  if (!hasApiKey) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <MessageCircleIcon />
          </EmptyMedia>
          <EmptyTitle>No API Key</EmptyTitle>
          <EmptyDescription>
            Add an API key via workspace settings to access the Social Media
            Agent.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <ChatKitPageWrapper
      user={user}
      userApiKey={userApiKey}
      workflowKey="social_media"
      constants={constants}
      t={t}
      icon={MessageCircleIcon}
    />
  )
}
